import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import { api } from "../../lib/api";

const CATEGORY_LABELS = {
    "getting-started-members": "Getting started — Members",
    "getting-started-advertisers": "Getting started — Advertisers",
    "payments-and-cashout": "Payments & cashout",
    "trust-and-safety": "Trust & safety",
    "privacy-and-data": "Privacy & data",
    "troubleshooting": "Troubleshooting",
};

export default function HelpIndex() {
    const [params, setParams] = useSearchParams();
    const category = params.get("category") || "";
    const q = params.get("q") || "";
    const [draftQ, setDraftQ] = useState(q);
    const [items, setItems] = useState(null);
    const [cats, setCats] = useState([]);

    useEffect(() => {
        const search = new URLSearchParams();
        if (category) search.set("category", category);
        if (q) search.set("q", q);
        api.get(`/kb/articles?${search.toString()}`).then((r) => setItems(r.data || []));
    }, [category, q]);

    useEffect(() => {
        api.get("/kb/categories").then((r) => setCats(r.data || []));
    }, []);

    const grouped = useMemo(() => {
        const map = new Map();
        (items || []).forEach((it) => {
            if (!map.has(it.category)) map.set(it.category, []);
            map.get(it.category).push(it);
        });
        return [...map.entries()];
    }, [items]);

    const submitSearch = (e) => {
        e.preventDefault();
        const next = new URLSearchParams(params);
        if (draftQ) next.set("q", draftQ); else next.delete("q");
        setParams(next, { replace: true });
    };

    return (
        <div className="bg-paper min-h-screen">
            <Navbar />
            <section className="max-w-5xl mx-auto px-6 py-16" data-testid="help-index">
                <p className="eyebrow mb-3">HELP CENTER</p>
                <h1 className="h1 mb-6">How can we help?</h1>

                <form onSubmit={submitSearch} className="mb-10 flex gap-2" data-testid="help-search-form">
                    <input
                        type="search"
                        value={draftQ}
                        onChange={(e) => setDraftQ(e.target.value)}
                        placeholder="Search articles…"
                        className="input-base flex-1"
                        data-testid="help-search-input"
                    />
                    <button type="submit" className="btn btn-primary" data-testid="help-search-submit">Search</button>
                </form>

                <div className="flex flex-wrap gap-2 mb-12" data-testid="help-category-chips">
                    <CategoryChip active={!category} to="/help" label={`All${items ? ` · ${items.length}` : ""}`} testid="help-cat-all" />
                    {cats.map((c) => (
                        <CategoryChip
                            key={c.slug}
                            active={category === c.slug}
                            to={`/help?category=${c.slug}`}
                            label={`${CATEGORY_LABELS[c.slug] || c.slug} · ${c.count}`}
                            testid={`help-cat-${c.slug}`}
                        />
                    ))}
                </div>

                {items === null && <p className="text-ink-600 font-mono text-sm">Loading…</p>}
                {items && items.length === 0 && (
                    <p className="text-ink-600" data-testid="help-empty-state">
                        No articles match. Try a different keyword or browse all categories.
                    </p>
                )}

                <div className="space-y-12">
                    {grouped.map(([catSlug, list]) => (
                        <section key={catSlug} data-testid={`help-section-${catSlug}`}>
                            <h2 className="text-sm uppercase tracking-wider text-ink-600 mb-4">
                                {CATEGORY_LABELS[catSlug] || catSlug}
                            </h2>
                            <ul className="grid sm:grid-cols-2 gap-4">
                                {list.map((art) => (
                                    <li key={art.id}>
                                        <Link
                                            to={`/help/${art.slug}`}
                                            className="block p-5 border border-ink-200 hover:border-ink hover:shadow-[4px_4px_0_0_rgba(0,0,0,1)] transition-all bg-paper"
                                            data-testid={`help-card-${art.slug}`}
                                        >
                                            <h3 className="font-semibold mb-1">{art.title}</h3>
                                            {art.excerpt && (
                                                <p className="text-sm text-ink-600 leading-relaxed">{art.excerpt}</p>
                                            )}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    ))}
                </div>
            </section>
            <Footer />
        </div>
    );
}

function CategoryChip({ active, to, label, testid }) {
    return (
        <Link
            to={to}
            data-testid={testid}
            className={`font-mono text-xs px-3 py-2 border ${active ? "bg-ink text-paper border-ink" : "bg-paper text-ink border-ink-300 hover:border-ink"}`}
        >
            {label}
        </Link>
    );
}
